import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { toast } from 'react-toastify';
import { errorHandler } from '@/utils/functions/errorHandler';
import useCompanyState from './company.state';
import useCompanyProfileCompletionState from './completion.state';

export interface IPunishment {
  id?: number | string;
  tenant_id?: number | string;
  org_chart_node_id?: number;
  org_chart_node?: {
    id: number;
    name: string;
  };
  name?: string;
  value?: string;
  description?: string;
  is_published?: boolean;
  attachment_file?: any;
  created_at?: string;
  updated_at?: string;
  fileUrl?: string;
}

interface PunishmentState {
  punishments: IPunishment[];
  selectedPunishment: IPunishment | null;
  isLoading: boolean;
  error: unknown;

  createPunishment: (tenantId: string | number, payload: Partial<IPunishment>) => Promise<void>;
  getPunishments: (tenantId: string | number) => Promise<void>;
  getPunishmentDetail: (tenantId: string | number, id: string | number) => Promise<IPunishment | void>;
  updatePunishment: (tenantId: string | number, id: string | number, payload: Partial<IPunishment>) => Promise<void>;
  deletePunishment: (tenantId: string | number, id: string | number) => Promise<void>;
  clearPunishments: () => void;
  setPunishment: (punishments: IPunishment[]) => void;
  bulkPublishPunishment: (tenantId: string | number, isPublished: boolean) => Promise<void>;
}

const usePunishmentState = create<PunishmentState>((set, get) => ({
  punishments: [],
  selectedPunishment: null,
  isLoading: false,
  error: null,

  setPunishment: punishments => set({ punishments }),
  clearPunishments: () => set({ punishments: [] }),

  createPunishment: async (tenantId, payload) => {
    set({ isLoading: true, error: null });
    try {
      const { uploadCompanyMedia } = useCompanyState.getState();

      if (payload.attachment_file) {
        const file = Array.isArray(payload.attachment_file) ? payload.attachment_file[0] : payload.attachment_file;

        const uploadRes = await uploadCompanyMedia(file, tenantId, 'punishment Attachment');
        const uploadedFile = uploadRes?.data?.[0] || uploadRes?.[0] || uploadRes;

        payload.attachment_file = JSON.stringify([
          {
            disk: uploadedFile.disk || 'minio',
            path: uploadedFile.path,
            filename: uploadedFile.filename,
            original_name: uploadedFile.original_name,
            mime_type: uploadedFile.mime_type,
            size: uploadedFile.size,
            extension: uploadedFile.extension,
            url: uploadedFile.url,
            uploaded_at: uploadedFile.uploaded_at || new Date().toISOString(),
            content_type: uploadedFile.content_type || uploadedFile.mime_type,
          },
        ]);
      }

      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/punishments/${tenantId}`;
      await axiosInstance.post(url, payload);

      await get().getPunishments(tenantId);
      
      const completionStore = useCompanyProfileCompletionState.getState();
      completionStore.fetchCompletion();
      toast.success('Punishment created successfully');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  updatePunishment: async (tenantId, id, payload) => {
    set({ isLoading: true });
    try {
      const { uploadCompanyMedia } = useCompanyState.getState();

      const attachment = payload.attachment_file;
      let firstFile: unknown;
      if (Array.isArray(attachment)) {
        firstFile = attachment[0];
      } else if (attachment instanceof File) {
        firstFile = attachment;
      } else {
        firstFile = (attachment as any)?.file || (attachment as any)?.originFileObj;
      }

      let actualFile: File | null;
      if (firstFile instanceof File) {
        actualFile = firstFile;
      } else if ((firstFile as any)?.originFileObj instanceof File) {
        actualFile = (firstFile as any).originFileObj;
      } else if ((firstFile as any)?.file instanceof File) {
        actualFile = (firstFile as any).file;
      } else {
        actualFile = null;
      }

      const hasNewFile = !!actualFile;

      if (hasNewFile) {
        const uploadRes = await uploadCompanyMedia(actualFile!, tenantId, 'punishment Attachment');
        const uploadedFile = uploadRes?.data?.[0] || uploadRes?.[0] || uploadRes;

        payload.attachment_file = JSON.stringify([
          {
            disk: uploadedFile.disk || 'minio',
            path: uploadedFile.path,
            filename: uploadedFile.filename,
            original_name: uploadedFile.original_name,
            mime_type: uploadedFile.mime_type,
            size: uploadedFile.size,
            extension: uploadedFile.extension,
            url: uploadedFile.url,
            uploaded_at: uploadedFile.uploaded_at || new Date().toISOString(),
            content_type: uploadedFile.content_type || uploadedFile.mime_type,
          },
        ]);
      } else {
        delete payload.attachment_file;
      }

      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/punishments/${tenantId}/${id}`;
      const { data } = await axiosInstance.put(url, payload);

      set(state => ({
        punishments: state.punishments.map(rw => (rw.id === id ? { ...rw, ...data.data } : rw)),
        selectedPunishment: data.data,
      }));

      toast.success('Punishment updated successfully');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getPunishments: async tenantId => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/punishments/${tenantId}`;
      const { data } = await axiosInstance.get(url);

      const punishmentsData = (data.data?.content || data.data || []).map((punishment: IPunishment) => {
        let fileUrl: string | null = null;
        if (punishment.attachment_file) {
          try {
            const files = JSON.parse(punishment.attachment_file);
            if (files.length > 0 && files[0].url) {
              fileUrl = `${axiosInstance.defaults.cloudStorage || 'https://minio.merapi.javan.id'}${files[0].url}`;
            }
          } catch {
            fileUrl = null;
          }
        }
        return { ...punishment, fileUrl };
      });

      set({ punishments: punishmentsData });
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getPunishmentDetail: async (tenantId, id) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/punishments/${tenantId}/${id}`;
      const { data } = await axiosInstance.get(url);
      set({ selectedPunishment: data.data });
      return data.data;
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  deletePunishment: async (tenantId, id) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/punishments/${tenantId}/${id}`;
      await axiosInstance.delete(url);
      set(state => ({
        punishments: state.punishments.filter(rw => rw.id !== id),
      }));
      
      const completionStore = useCompanyProfileCompletionState.getState();
      completionStore.fetchCompletion();
      toast.success('Punishment deleted successfully');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  bulkPublishPunishment: async (tenantId: string | number, isPublished: boolean) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/punishments/${tenantId}/bulk-publish`;
      await axiosInstance.put(url, { published: isPublished });

      set(state => ({
        punishments: state.punishments.map(ka => ({
          ...ka,
          published: isPublished,
        })),
        isLoading: false,
      }));

      toast.success(isPublished ? 'All punishments published successfully' : 'All punishments unpublished successfully');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },
}));

export default usePunishmentState;
